import { asc, eq, inArray } from "drizzle-orm";
import {
  block,
  type Block,
  page,
  type Page,
  type PageVersion,
  type PowerSyncDrizzleDB,
} from "@apunte-digital/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateId } from "@fleet/utils";
import { waitForRowsPresent, writeVerified } from "../powersync/write-verify";
import { sortKeyBetween } from "./sort-key";
import { collectDescendantPageIds, isPageInSubtree } from "./tree";

/**
 * Compares the fields of a partial `page` patch against the reread row, field
 * by field and using the real `Page` keys — without casting to
 * `Record<string, unknown>` (unsafe: `row` never has a string index).
 * A key absent from `patch` (it was not requested to change) is not checked;
 * everything else must match exactly, including `updated_at`.
 */
function pagePatchMatches(
  row: Page,
  patch: Partial<Pick<Page, "title" | "icon" | "parent_page_id" | "sort_key">> & {
    updated_at: string;
  },
): boolean {
  return (
    (patch.title === undefined || row.title === patch.title) &&
    (patch.icon === undefined || row.icon === patch.icon) &&
    (patch.parent_page_id === undefined || row.parent_page_id === patch.parent_page_id) &&
    (patch.sort_key === undefined || row.sort_key === patch.sort_key) &&
    row.updated_at === patch.updated_at
  );
}

/**
 * Creates a new page. Returns its id.
 */
export async function createPageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  opts?: {
    title?: string;
    icon?: string;
    parentPageId?: string;
    /** Key of the page after which to insert (null = at the end). */
    afterSortKey?: string | null;
    beforeSortKey?: string | null;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(page).values({
    id,
    user_id: userId,
    title: opts?.title ?? "",
    icon: opts?.icon ?? null,
    parent_page_id: opts?.parentPageId ?? null,
    sort_key: sortKeyBetween(opts?.afterSortKey ?? null, opts?.beforeSortKey ?? null),
    is_shared: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * Updates an existing page.
 *
 * §4.11: against the PowerSync view, this UPDATE by primary key can match ZERO
 * rows and return "success" if the page was just created and the CRUD queue
 * emptied in the middle (the checkpoint is rebuilding the view). Verified with
 * `writeVerified`: write → reread → compare → retry with backoff → throw if the
 * change never sticks. It is never silently lost.
 */
export async function updatePageMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    title: string | null;
    icon: string | null;
    parent_page_id: string | null;
    sort_key: string;
  }>,
): Promise<void> {
  const patch = { ...updates, updated_at: nowISO() };
  await writeVerified({
    write: async () => {
      await db.update(page).set(patch).where(eq(page.id, id));
    },
    reread: () => db.select().from(page).where(eq(page.id, id)),
    verify: (rows) => {
      const [row] = rows;
      if (!row) {
        return false;
      }
      return pagePatchMatches(row, patch);
    },
    describe: `página ${id}`,
  });
}

/**
 * PHYSICALLY deletes a page, its blocks, and all its subpages (recursively).
 * This is the trash's "delete permanently": it is irreversible and also cascades
 * through the version history (FK in Postgres).
 * Postgres performs the cascade on the server; local SQLite does it explicitly
 * so the UI reflects the deletion immediately.
 */
export async function deletePageMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  const allPages = await db.select({ id: page.id, parent_page_id: page.parent_page_id }).from(page);
  const toDelete = collectDescendantPageIds(allPages, id);

  await db.transaction(async (tx) => {
    await tx.delete(block).where(inArray(block.page_id, toDelete));
    await tx.delete(page).where(inArray(page.id, toDelete));
  });
}

/**
 * Trash (L2): soft-deletes the page and its ENTIRE still-living subtree, with
 * the SAME timestamp (cohort). Restoring the parent restores exactly its
 * cohort; a subpage sent to the trash EARLIER keeps its own timestamp and is
 * not restored by the cascade.
 */
export async function movePageToTrashMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  const allPages = await db
    .select({ id: page.id, parent_page_id: page.parent_page_id, deleted_at: page.deleted_at })
    .from(page);
  const subtree = new Set(collectDescendantPageIds(allPages, id));
  const alive = allPages
    .filter((row) => subtree.has(row.id) && row.deleted_at === null)
    .map((row) => row.id);
  if (alive.length === 0) {
    return;
  }
  const now = nowISO();
  // §4.11 in batches: the same zero-row UPDATE risk applies to the entire
  // cohort (a newly created subpage not yet visible in the view).
  await writeVerified({
    write: async () => {
      await db
        .update(page)
        .set({ deleted_at: now, updated_at: now })
        .where(inArray(page.id, alive));
    },
    reread: () =>
      db
        .select({ id: page.id, deleted_at: page.deleted_at, updated_at: page.updated_at })
        .from(page)
        .where(inArray(page.id, alive)),
    verify: (rows) =>
      rows.length === alive.length &&
      rows.every((row) => row.deleted_at === now && row.updated_at === now),
    describe: `papelera de ${String(alive.length)} página(s) (raíz ${id})`,
  });
}

/**
 * Restores a page from the trash together with the cohort of subpages that
 * fell with it (the same deleted_at). If its parent no longer exists or is
 * still in the trash, the restored page moves to the root (it does not remain
 * an invisible orphan).
 */
export async function restorePageFromTrashMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  const allPages = await db
    .select({ id: page.id, parent_page_id: page.parent_page_id, deleted_at: page.deleted_at })
    .from(page);
  const target = allPages.find((row) => row.id === id);
  if (!target || target.deleted_at === null) {
    return;
  }
  const cohortTs = target.deleted_at;
  const subtree = new Set(collectDescendantPageIds(allPages, id));
  const cohort = allPages
    .filter((row) => subtree.has(row.id) && row.deleted_at === cohortTs)
    .map((row) => row.id);

  const parent = allPages.find((row) => row.id === target.parent_page_id);
  const orphaned = target.parent_page_id !== null && (!parent || parent.deleted_at !== null);

  const now = nowISO();

  // §4.11 case INSIDE A TRANSACTION: before opening the transaction, confirm
  // that the anchor row (`id`) is really present in the view — if `id` was
  // created and sent to the trash in the same offline burst, the checkpoint
  // may not have downloaded it yet, and the correlated UPDATE below would
  // match zero rows inside the transaction without anyone noticing.
  await waitForRowsPresent({
    reread: () => db.select({ id: page.id }).from(page).where(eq(page.id, id)),
    verify: (rows) => rows.length === 1,
    describe: `página ancla ${id} antes de restaurar de la papelera`,
  });

  // And after closing the transaction, reread and compare: the batch UPDATE
  // for the cohort has the SAME zero-row matching risk as the rest of the
  // writes in this file.
  await writeVerified({
    write: async () => {
      await db.transaction(async (tx) => {
        await tx
          .update(page)
          .set({ deleted_at: null, updated_at: now })
          .where(inArray(page.id, cohort));
        if (orphaned) {
          await tx
            .update(page)
            .set({ parent_page_id: null, updated_at: now })
            .where(eq(page.id, id));
        }
      });
    },
    reread: () =>
      db
        .select({
          id: page.id,
          deleted_at: page.deleted_at,
          updated_at: page.updated_at,
          parent_page_id: page.parent_page_id,
        })
        .from(page)
        .where(inArray(page.id, cohort)),
    verify: (rows) => {
      if (rows.length !== cohort.length || !rows.every((row) => row.deleted_at === null)) {
        return false;
      }
      if (!orphaned) {
        return true;
      }
      const anchor = rows.find((row) => row.id === id);
      return anchor?.parent_page_id === null;
    },
    describe: `restaurar de la papelera ${String(cohort.length)} página(s) (raíz ${id})`,
  });
}

/**
 * Moves a page to another parent and/or position. Ignores the operation if it
 * would create a cycle (dropping a page inside its own subtree).
 */
export async function movePageMutation(
  db: PowerSyncDrizzleDB,
  pageId: string,
  target: {
    parentPageId: string | null;
    /** Key of the previous sibling at the destination position (null = first). */
    afterSortKey?: string | null;
    /** Key of the next sibling (null = last). */
    beforeSortKey?: string | null;
  },
): Promise<void> {
  if (target.parentPageId) {
    const allPages = await db
      .select({ id: page.id, parent_page_id: page.parent_page_id })
      .from(page);
    if (isPageInSubtree(allPages, pageId, target.parentPageId)) {
      return;
    }
  }

  const patch = {
    parent_page_id: target.parentPageId,
    sort_key: sortKeyBetween(target.afterSortKey ?? null, target.beforeSortKey ?? null),
    updated_at: nowISO(),
  };
  // §4.11: moving a newly created page (immediate drag & drop after creating it)
  // is exactly the defect's "you edit the newly created note" case.
  await writeVerified({
    write: async () => {
      await db.update(page).set(patch).where(eq(page.id, pageId));
    },
    reread: () => db.select().from(page).where(eq(page.id, pageId)),
    verify: (rows) => {
      const [row] = rows;
      if (!row) {
        return false;
      }
      return pagePatchMatches(row, patch);
    },
    describe: `mover página ${pageId}`,
  });
}

/**
 * Top-level editor node, ready to persist as a block.
 */
export interface EditorBlockNode {
  /** Stable block UUID (attrs.blockId of the TipTap node). */
  id: string;
  /** TipTap node type (paragraph, heading, bulletList, …). */
  type: string;
  /** Serialized JSON of the complete node. */
  content: string;
  /** Plain text extracted from the node (LIKE search without JSON noise). */
  text: string;
}

/**
 * Synchronizes a page's blocks with the editor's current state.
 *
 * "One block = one row" strategy: diff by id between the top-level nodes of
 * the TipTap document and the local rows. Only changed blocks are written
 * (insert/update/delete), so editing a paragraph generates ONE sync operation,
 * not a rewrite of the document.
 */
export async function syncPageBlocksMutation(
  db: PowerSyncDrizzleDB,
  pageId: string,
  userId: string,
  nodes: EditorBlockNode[],
): Promise<void> {
  const keptIds = new Set(nodes.map((node) => node.id));
  const now = nowISO();

  // §4.11: block UPDATE/INSERTs run inside a transaction, so they cannot be
  // verified one by one — the ENTIRE transaction is verified
  // (write → reread the page's blocks → compare against `nodes` → retry with
  // backoff). The diff (`existing`/`removedIds`) is RECALCULATED on each attempt
  // instead of once outside: if an earlier attempt inserted a row the view did
  // not yet reflect when reread, a new attempt with the STALE diff would
  // re-insert it as if it did not exist → PK collision. Reading fresh each
  // time, an already materialized block appears as `current` and takes the
  // UPDATE branch (or no-op if unchanged), never a second INSERT.
  await writeVerified({
    write: async () => {
      const existing: Block[] = await db
        .select()
        .from(block)
        .where(eq(block.page_id, pageId))
        .orderBy(asc(block.sort_key), asc(block.created_at));
      const existingById = new Map(existing.map((row) => [row.id, row]));
      const removedIds = existing.filter((row) => !keptIds.has(row.id)).map((row) => row.id);

      // One local transaction ⇒ ONE PowerSync transactionId ⇒ the server
      // processes the entire save as one group (and version history archives
      // ONE version per save, not one per touched block).
      await db.transaction(async (tx) => {
        if (removedIds.length > 0) {
          await tx.delete(block).where(inArray(block.id, removedIds));
        }

        let prevKey: string | null = null;

        for (let i = 0; i < nodes.length; i++) {
          const node = nodes[i];
          if (!node) {
            continue;
          }
          const current = existingById.get(node.id);
          let key = current?.sort_key ?? null;

          // The existing key is only useful if it respects document order
          const keyIsValid = key !== null && (prevKey === null || key > prevKey);
          if (!keyIsValid) {
            // Upper bound: the key of the next surviving block that still
            // orders after prevKey (if there is none, insert "at the end").
            let upper: string | null = null;
            for (let j = i + 1; j < nodes.length; j++) {
              const nextNode = nodes[j];
              const candidate = nextNode ? (existingById.get(nextNode.id)?.sort_key ?? null) : null;
              if (candidate !== null && (prevKey === null || candidate > prevKey)) {
                upper = candidate;
                break;
              }
            }
            key = sortKeyBetween(prevKey, upper);
          }

          if (!current) {
            await tx.insert(block).values({
              id: node.id,
              page_id: pageId,
              user_id: userId,
              type: node.type,
              content: node.content,
              text: node.text,
              sort_key: key,
              is_shared: 0,
              created_at: now,
              updated_at: now,
            });
          } else if (
            current.content !== node.content ||
            current.type !== node.type ||
            current.sort_key !== key
          ) {
            await tx
              .update(block)
              .set({
                type: node.type,
                content: node.content,
                text: node.text,
                sort_key: key,
                updated_at: now,
              })
              .where(eq(block.id, node.id));
          }

          prevKey = key;
        }
      });
    },
    reread: () => db.select().from(block).where(eq(block.page_id, pageId)),
    verify: (rows) => {
      const byId = new Map(rows.map((row) => [row.id, row]));
      return nodes.every((node) => {
        const row = byId.get(node.id);
        return (
          row !== undefined &&
          row.content === node.content &&
          row.type === node.type &&
          row.text === node.text
        );
      });
    },
    describe: `bloques de la página ${pageId}`,
  });
}

/** A block as it travels inside a version's `blocks` snapshot. */
interface VersionBlockNode {
  id: string;
  type: string;
  content: string | null;
  text: string | null;
  sort_key: string;
}

function isNullOrString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isVersionBlockNode(value: unknown): value is VersionBlockNode {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const node: Partial<Record<keyof VersionBlockNode, unknown>> = value;
  return (
    typeof node.id === "string" &&
    typeof node.type === "string" &&
    typeof node.sort_key === "string" &&
    isNullOrString(node.content) &&
    isNullOrString(node.text)
  );
}

/** Parses the `blocks` snapshot (JSON from the local bucket or the API). */
export function parseVersionBlocks(blocks: string | null): VersionBlockNode[] {
  if (!blocks) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(blocks);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((node) => isVersionBlockNode(node));
  } catch {
    return [];
  }
}

/**
 * Restores a version from history (L4): a NORMAL forward write.
 *
 * History is never rewritten: the server write-path archives the current state
 * as a new version BEFORE applying this (forced by the
 * `restored_from_version` marker, which skips autosave's temporal collapse).
 *
 * The `page` UPDATE goes FIRST in the transaction: its op reaches the server
 * before the block changes, so the pre-restore snapshot captures the blocks
 * while they are still intact.
 */
export async function restorePageVersionMutation(
  db: PowerSyncDrizzleDB,
  pageId: string,
  version: Pick<PageVersion, "id" | "title" | "icon" | "blocks">,
): Promise<void> {
  const [pageRow] = await db
    .select({ user_id: page.user_id })
    .from(page)
    .where(eq(page.id, pageId))
    .limit(1);
  if (!pageRow) {
    return;
  }
  const snapshot = parseVersionBlocks(version.blocks);
  const keptIds = new Set(snapshot.map((node) => node.id));
  const now = nowISO();
  const pagePatch = {
    title: version.title ?? "",
    icon: version.icon,
    restored_from_version: version.id,
    updated_at: now,
  };

  // §4.11: same as `syncPageBlocksMutation` — the entire transaction
  // (`page` UPDATE + block insert/update) is verified as a whole, recalculating
  // the block diff INSIDE `write` on each attempt so a retry never re-inserts a
  // block that an earlier attempt already materialized (avoiding a PK collision;
  // see the comment in `syncPageBlocksMutation`).
  await writeVerified({
    write: async () => {
      const existing: Block[] = await db.select().from(block).where(eq(block.page_id, pageId));
      const existingById = new Map(existing.map((row) => [row.id, row]));
      const removedIds = existing.filter((row) => !keptIds.has(row.id)).map((row) => row.id);

      await db.transaction(async (tx) => {
        // The `page` UPDATE goes FIRST in the transaction: its op reaches the
        // server before the block changes (see the function's documentation).
        await tx.update(page).set(pagePatch).where(eq(page.id, pageId));

        if (removedIds.length > 0) {
          await tx.delete(block).where(inArray(block.id, removedIds));
        }
        for (const node of snapshot) {
          const current = existingById.get(node.id);
          if (!current) {
            await tx.insert(block).values({
              id: node.id,
              page_id: pageId,
              user_id: pageRow.user_id,
              type: node.type,
              content: node.content,
              text: node.text,
              sort_key: node.sort_key,
              is_shared: 0,
              created_at: now,
              updated_at: now,
            });
          } else if (
            current.content !== node.content ||
            current.type !== node.type ||
            current.text !== node.text ||
            current.sort_key !== node.sort_key
          ) {
            await tx
              .update(block)
              .set({
                type: node.type,
                content: node.content,
                text: node.text,
                sort_key: node.sort_key,
                updated_at: now,
              })
              .where(eq(block.id, node.id));
          }
        }
      });
    },
    reread: async () => {
      const [pageRowNow] = await db.select().from(page).where(eq(page.id, pageId)).limit(1);
      const blockRows = await db.select().from(block).where(eq(block.page_id, pageId));
      return [{ pageRow: pageRowNow, blockRows }];
    },
    verify: (results) => {
      const [result] = results;
      const pageRowNow = result?.pageRow;
      if (!pageRowNow) {
        return false;
      }
      const pageMatches =
        pageRowNow.title === pagePatch.title &&
        pageRowNow.icon === pagePatch.icon &&
        pageRowNow.restored_from_version === pagePatch.restored_from_version &&
        pageRowNow.updated_at === pagePatch.updated_at;
      if (!pageMatches) {
        return false;
      }
      const byId = new Map(result.blockRows.map((row) => [row.id, row]));
      return snapshot.every((node) => {
        const row = byId.get(node.id);
        return (
          row !== undefined &&
          row.content === node.content &&
          row.type === node.type &&
          row.text === node.text
        );
      });
    },
    describe: `restaurar versión ${version.id} de la página ${pageId}`,
  });
}
